import type React from 'react';
import { useMemo, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SWReactIcons } from '@similarweb/icons';
import { colorsPalettes } from '@similarweb/styles';

import type { Company } from '../../../domain/company';
import { Crm } from '../../../domain/crm';
import type { SalesforceNormalizedEntity } from '../../../domain/salesforceActivity';
import { ALL_CATEGORY, SalesforceActivityMatchingStatus } from '../../../domain/salesforceActivity';
import { useExportAccountToCrmMutation } from '../../../features/crm/exportAccountToCrm/exportAccountToCrmMutation';
import { useCrmStatusQuery } from '../../../features/crm/useCrmStatusQuery';
import { useSalesforceActivityQuery } from '../../../features/salesforceActivity/useSalesforceActivityQuery';
import { useUserQuery } from '../../../features/user/useUserQuery';
import { useCompanyActionTracking } from '../CompanyActionsContainer/useCompanyActionTracking';
import { SectionHeader } from '../SectionHeader';
import { StyledSectionView } from '../styles';

import { AccountFilter } from './components/AccountFilter/AccountFilter';
import { SalesforceActivityContent } from './components/SalesforceActivityContent/SalesforceActivityContent';
import { computeDefaultEntity, getEntityIdForTask, getTasksForEntity } from './utils/extractors';
import { useSalesforceActivityTracking } from "./hooks/useSalesforceActivityTracking";
import { StyledSalesforceActivityTitle } from './styles';

export const SalesforceActivity: React.FC<{ company: Company }> = ({ company }) => {
  const { t } = useTranslation();

  const [activeCategory, setActiveCategory] = useState<string>(ALL_CATEGORY);
  const [selectedEntity, setSelectedEntity] = useState<SalesforceNormalizedEntity | undefined>(undefined);

  const { domain } = company;

  const {
    data: salesforceStatusData,
    isLoading: isSalesforceStatusLoading,
    isError: isSalesforceStatusError,
  } = useCrmStatusQuery(Crm.salesforce);

  const isSalesforceEnabled = salesforceStatusData?.isCrmFeedEnabled;

  const {
    data,
    isFetching,
    isError: isSalesforceActivityError,
  } = useSalesforceActivityQuery(domain, isSalesforceEnabled);
  const { mutate, isPending: isExporting } = useExportAccountToCrmMutation();
  const { data: userData } = useUserQuery();

  const isCrmAdmin = userData?.hasAccessToManageCrm;
  const isInternalUser = userData?.isInternalUser;

  const { handleTrackExportAction } = useCompanyActionTracking();
  const { handleTrackViewInSalesforce, handleTrackManageCTAClick } = useSalesforceActivityTracking();

  const isLoading = isFetching || isSalesforceStatusLoading;
  const isError = isSalesforceActivityError || isSalesforceStatusError;
  const accounts = data?.accounts ?? [];
  const leads = data?.leads ?? [];
  const tasks = data?.tasks ?? [];
  const isFoundButNotOwned = data?.matchingStatus === SalesforceActivityMatchingStatus.FoundButNotOwned;

  const entities: readonly SalesforceNormalizedEntity[] = useMemo(() => {
    const normalizedAccounts = accounts.map(
      (account): SalesforceNormalizedEntity => ({
        ...account,
        description: account.type,
        subType: 'account',
        taskCount: getTasksForEntity(account.id, tasks).length,
      })
    );
    const normalizedLeads = leads.map(
      (lead): SalesforceNormalizedEntity => ({
        ...lead,
        description: lead.status,
        subType: 'lead',
        taskCount: getTasksForEntity(lead.id, tasks).length,
      })
    );

    return [...normalizedAccounts, ...normalizedLeads].sort((a, b) => b.taskCount - a.taskCount);
  }, [accounts, leads, tasks]);

  const defaultEntity = useMemo(() => computeDefaultEntity(entities, tasks), [entities, tasks]);

  const activeEntity = selectedEntity ?? defaultEntity ?? null;

  const categories = useMemo(() => {
    const uniqueSubtypes = [...new Set(tasks.map((task) => task.taskSubtype))];
    return [ALL_CATEGORY, ...uniqueSubtypes];
  }, [tasks]);

  const filteredTasks = useMemo(() => {
    let result = tasks;

    if (activeEntity) {
      result = result.filter((task) => getEntityIdForTask(task) === activeEntity.id);
    }

    if (activeCategory !== ALL_CATEGORY) {
      result = result.filter((task) => task.taskSubtype === activeCategory);
    }

    return result;
  }, [tasks, activeCategory, activeEntity]);

  const isEmpty = (!entities.length && !isLoading) || isError;

  const handleEntityChange = (entity: SalesforceNormalizedEntity) => {
    setSelectedEntity(entity);
  };

  const handleExportToSalesforce = () => {
    const crm = Crm.salesforce;

    handleTrackExportAction('export company', crm);

    mutate({
      domain,
      crm,
      onSuccess: () => handleTrackExportAction('export success', crm),
      onError: () => handleTrackExportAction('export failed', crm),
    });
  };

  const titleElement = (
    <StyledSalesforceActivityTitle>
      <SWReactIcons size="xs" iconName="salesforce" iconColor={colorsPalettes.carbon[200]} />
      <span>{t('company.salesforce_activity.title')}</span>
    </StyledSalesforceActivityTitle>
  );

  const titleAdditionalElement =
    activeEntity && !isEmpty && isSalesforceEnabled && !isFoundButNotOwned ? (
      <AccountFilter entities={entities} defaultEntity={activeEntity} onEntityChange={handleEntityChange} />
    ) : undefined;

  return (
    <StyledSectionView withPadding data-automation="CompanyOverviewSalesforceActivity">
      <SectionHeader withSectionPadding title={titleElement} titleAdditionalComponent={titleAdditionalElement} />

      <SalesforceActivityContent
        isLoading={isLoading}
        isExporting={isExporting}
        isEmpty={isEmpty}
        isSalesforceEnabled={isSalesforceEnabled}
        isUserCanManageCRM={Boolean(isCrmAdmin && isInternalUser)}
        isFoundButNotOwned={isFoundButNotOwned}
        filteredTasks={filteredTasks}
        categories={categories}
        salesforceLink={activeEntity?.link}
        selectedCategory={activeCategory}
        onSelectCategory={setActiveCategory}
        onExportToSalesforce={handleExportToSalesforce}
        onSalesforceLinkClick={handleTrackViewInSalesforce}
        onManageCTAClick={() => handleTrackManageCTAClick(domain)}
      />
    </StyledSectionView>
  );
};
